import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { HelpCircle, ChevronDown } from 'lucide-react';
export function FAQSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    question: 'Can I cancel my membership early?',
    answer: `No.

This is a 12-month commitment.

If you choose the monthly payment option, you're committing to all 12 months.

If you cancel early, you lose access to all course materials and membership benefits, and you forfeit your Founding Member status.

This is designed for serious individuals who are committed to transformation, not casual browsers.`
  }, {
    question: 'What if I already own the original Peptide Mastery?',
    answer: `You already have lifetime access to Peptide Mastery with all updates (including the 2.0 version).

You can join the Advanced Intelligence Agency at the special alumni rate.

This gives you the 12 months of live access to Brenden and Ali without paying again for the course content you already own.`
  }, {
    question: 'How are the live sessions delivered?',
    answer: `All live sessions will initially be delivered via Google Meet.

If you can't attend live, all sessions are recorded and available in the members area for members who are currently active and were active when the session was recorded.

You will be able to watch on your own schedule and still get the full value.`
  }, {
    question: 'What if I have questions between live sessions?',
    answer: `You can submit questions in advance for the upcoming Q&A sessions.

Brenden and Ali will address as many appropriate member questions as possible during each session.

The beauty of 3+ sessions per month is that you're never more than 1-2 weeks away from your next opportunity to get answers.

For urgent questions, the private member community (once implemented) will allow for peer support and occasional expert input.`
  }, {
    question: 'Is there a community forum or group?',
    answer: `Not at launch.

We're focusing on delivering exceptional live content first.

But we will look into adding a private community later based on member feedback and what would serve you best.`
  }, {
    question: 'Will this price ever be available again?',
    answer: `No.

This is a one-time offer.

Once the deadline passes (Friday, December 5th at 11:59 PM ET), this exact bundle at this price disappears forever.

We may reopen the Advanced Intelligence Agency in the future, but it will be at a higher price with fewer bonuses.

This is your only chance to lock in this rate.`
  }, {
    question: 'What makes this different from other peptide courses?',
    answer: `Depth: No other course covers 130+ peptides with this level of scientific rigor backed by 1,193 citations.

Source Access: Created by the most knowledgeable person in the world when it comes to peptide science.

Protocols: The 54 Brenden Henry protocols are completely original, precision-engineered through years of research and 1,500+ real human experiments, and unavailable anywhere else.

Verification: Mass spec testing shows you what's real vs. fake in the market, potentially saving you thousands in wasted purchases.

Live Access: 12 months of direct retainer access to the world's leading peptide science authority (Brenden) and a "unicorn" diagnostic genius with 8 different medical specialties (Ali) who spots what entire medical teams miss.

Updates: Lifetime access to all future course updates as the science evolves.

The Book Connection: This is created by the authors of Peptide Salvation – the most scientifically validated book on peptides ever written. If you've read the book, this is the implementation system that brings it to life.

There is literally nothing else like this in the world.`
  }, {
    question: "I'm new to peptides. Is this too advanced for me?",
    answer: `No.

Peptide Mastery 2.0 is designed to take you from beginner to expert.

The course starts with foundational concepts and progressively builds to advanced applications.

The live Q&A sessions allow you to ask basic questions and get clear answers from the masters.

You'll be learning alongside others at various levels, and the monthly themes help you build comprehensive understanding systematically.

In fact, being NEW to peptides might be an advantage.

Because you won't have to unlearn bad information you picked up from influencers and Reddit threads.

You'll be able to get going from day one instead of wasting months on trial and error.`
  }, {
    question: 'Can women benefit from this?',
    answer: `Absolutely.

While some protocols are gender-specific (we have separate modules for male and female hormone optimization), the vast majority of the content applies to both men and women.

The live sessions address questions from all members regardless of gender.

And many of our most successful clients and students are women achieving remarkable transformations in:

Anti-aging and longevity
Cognitive enhancement
Autoimmune condition reversal
Metabolic optimization
Hormone balance
Recovery and regeneration`
  }, {
    question: 'Do I need to buy peptides separately?',
    answer: `Yes.

The course teaches you WHAT to use, HOW to use it, and WHERE to get it.

But you'll need to purchase peptides from recommended suppliers.

We provide:

Guidance on high-quality vendors
Mass spec purity reports to help you avoid bunk products
Membership-only discount codes that can significantly reduce your costs

As an Advanced Intelligence Agency member, you may receive exclusive discount codes throughout the year that can help increasingly offset your membership cost to higher levels.`
  }, {
    question: "What if I'm working with a doctor or coach already?",
    answer: `This is a perfect complement to any existing health optimization program.

In fact, many of our students ARE doctors, coaches, and practitioners who use this knowledge to enhance their own practice.

You can share what you learn with your existing providers (many clients report their doctors are amazed at the results and want to know more).

Or you can use this as your primary source of biological optimization intelligence.

Either way, the knowledge is yours to apply however serves you best.`
  }, {
    question: 'How much time do I need to commit each week?',
    answer: `That's up to you.

The live sessions are 3+ times per month (roughly 3-4 hours of live content monthly).

You can watch recordings if you can't attend live.

Beyond that, you can go through the Peptide Mastery 2.0 course materials at your own pace.

Some members binge entire sections in a weekend.

Others go slowly and methodically, implementing one protocol at a time and mastering it before moving to the next.

The course and membership are designed to fit YOUR schedule and learning style.

Minimum time commitment: ~1 hour per week (watching session recordings)

Optimal time commitment: 3-5 hours per week (attending live sessions + implementing protocols)`
  }, {
    question: 'What happens after 12 months?',
    answer: `You keep lifetime access to Peptide Mastery 2.0 and all future updates.

Your Advanced Intelligence Agency membership ends after 12 months (unless we offer a renewal option at that time).

If you've completed the full 12 months without canceling, you earn:

Permanent Founding Member status
First priority invitation to apply for The Elite tier ($25,000/year strategic retainer with ongoing personalized guidance from Brenden or Ali)`
  }, {
    question: 'Why is there no money-back guarantee?',
    answer: `Because this is for committed individuals only.

We've found that the only people who request refunds are those who shouldn't have bought in the first place.

They weren't ready.

They didn't actually value the expertise.

They were looking for a magic pill instead of systematic optimization.

If you need a guarantee to feel safe investing, you're not ready for this level of training.

Our commitment is to deliver world-class intelligence.

Your commitment is to show up, absorb, and apply it.

If you're not 100% certain this is for you, don't buy.`
  }, {
    question: 'What if I have a specific health condition? Can this help?',
    answer: `We have protocols for dozens of specific conditions:

Alzheimer's & cognitive decline
Parkinson's disease
Diabetes (types 1 and 2) & insulin resistance
Autoimmune conditions (rheumatoid arthritis, Graves' disease, etc.)
Cardiovascular issues
Liver disease
Kidney disease
Chronic pain & inflammation
Neuropathy
Traumatic brain injury
Stroke recovery
Cancer recovery support
And many more...

During the live Q&A sessions, you can ask about your specific situation and get direct guidance from Brenden and Ali.

Many of our most dramatic client transformations involve conditions mainstream medicine calls "incurable."

But please be aware that those results were achieved with private transformation programs, and a group Q&A session cannot properly or completely substitute for dedicated private guidance.

However, the course itself and our team's knowledge will be available to you.`
  }, {
    question: 'Can I gift this to someone?',
    answer: `Yes.

You can purchase on behalf of someone else and transfer access to them.

Just reach out to our support team after purchase with the recipient's information and we can help you set that up.

This makes an extraordinary gift for someone you care about who's serious about health optimization.`
  }, {
    question: 'When does the live membership start?',
    answer: `We will begin onboarding you for The Advanced Intelligence Agency on Friday December 12th, 2025.

But when you join now, you get immediate access to Peptide Mastery 2.0 so you can start learning and implementing protocols right away.

This gives you time to go through the foundational material before the live sessions begin, so you can ask more advanced questions when we start.`
  }, {
    question: "I've read Peptide Salvation. Is this just the same information?",
    answer: `No – this is Peptide Salvation on steroids.

Peptide Salvation is the master book – the most scientifically validated reference on peptides ever written.

Peptide Mastery 2.0 takes that foundation and:

Expands it 10x with video modules, detailed protocols, and implementation guidance
Adds 54 complete synergistic protocols you can implement immediately
Includes mass spec verification so you know what products are actually legitimate
Provides lifetime updates as new research emerges
Gives you bonus modules on precision stacking and advanced applications
And MUCH MORE.

The Advanced Intelligence Agency then adds:

12 months of live access to Brenden and Ali (the authors themselves)
36+ live sessions where you can ask questions and get personalized guidance
Monthly themed intelligence that goes deeper into specific optimization areas
Updates on new research and protocols
Case study breakdowns showing exactly how impossible transformations were achieved

If Peptide Salvation is the first encyclopedia...

Peptide Mastery 2.0 is the EXPANDED encyclopedia AND the implementation system...

And The Advanced Intelligence Agency is having the masters themselves guide you personally for a year.`
  }];
  return;
}